'use client'

import { createContext, useContext, useState, useCallback } from 'react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { useTranslations } from 'next-intl'
import { useAIMedia } from '@/hooks/use-ai-media'
import { useClearCreationLibraryCache } from '@/hooks/use-clear-swr-cache'
import type { AICreation } from './types'
import { popModal } from '@/components/modals'
import { nanoid } from 'nanoid'

interface AIGeneratorContextType {
  desc: string
  setDesc: (desc: string) => void
  activeType: string
  setActiveType: (type: string) => void
  resources: AICreation[]
  setResources: (resources: AICreation[]) => void
  createDisable: boolean
  isUploading: boolean
  selectedRatio: string
  setSelectedRatio: (ratio: string) => void
  isTooltipOpen: boolean
  setIsTooltipOpen: (open: boolean) => void
  isEditing: boolean
  setIsEditing: (editing: boolean) => void
  canUpload: boolean
  aiGenerator: () => Promise<void>
  handleAddToList: () => Promise<void>
  handleRemoveImage: (url: string) => void
  handleRatioSelect: (ratio: string) => void
  handleTooltipClick: () => void
  handleEditPrompt: () => void
}

const AIGeneratorContext = createContext<AIGeneratorContextType | undefined>(
  undefined,
)

export function AIGeneratorProvider({
  children,
  onAddToLibraryComplete,
  onGenerationStart,
  onGenerationEnd,
}: {
  children: ReactNode
  onAddToLibraryComplete?: () => void
  onGenerationStart?: (count: number) => void
  onGenerationEnd?: () => void
}) {
  const t = useTranslations('modals.ai-generator-v2')
  const { aiMedia } = useAIMedia()
  const { clear: clearCreationLibraryCache } = useClearCreationLibraryCache()

  const [desc, setDesc] = useState('')
  const [activeType, setActiveType] = useState('image')
  const [resources, setResources] = useState<AICreation[]>([])
  const [createDisable, setCreateDisable] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [selectedRatio, setSelectedRatio] = useState('3:4')
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(true)

  const canUpload = resources.some((img) => img.isSelected)

  const aiGenerator = async () => {
    setResources([])
    setCreateDisable(true)
    setIsEditing(true)

    try {
      if (activeType === 'image') {
        const response = await aiMedia({
          model: 'replicate/black-forest-labs/flux-schnell',
          inputs: {
            prompt: desc,
            go_fast: true,
            megapixels: '1',
            num_outputs: 4,
            aspect_ratio: selectedRatio,
          },
        })

        if (response?.code === 500) {
          if (response?.message?.includes('No features in text')) {
            toast.error(t('please_input_specific_requirements'))
          } else {
            toast.error(t('generating_error'))
          }
          return
        }

        if (response !== undefined && !response.code) {
          const newImages = response.map((url: string) => ({
            thumbnail: url,
            url: url,
            isSelected: true,
            type: 'image' as const,
            prompt: desc,
          }))

          setResources(newImages)
          setIsEditing(false)
        }
      } else {
        // DISABLED: Video generation is too expensive
        toast.error(
          'Video generation is currently disabled due to high costs. Please use images or GIFs instead.',
        )
        console.warn(
          'Video generation disabled (cost optimization). Requested prompt:',
          desc,
        )
        return
        // const response = await aiMedia({
        //   model: 'minimax/video-01',
        //   inputs: { prompt: desc },
        // })

        // if (response?.code === 500) {
        //   toast.error(t('generating_error'))
        //   return
        // }

        // if (
        //   response !== undefined &&
        //   response.base_resp?.status_msg === 'success'
        // ) {
        //   setResources([
        //     {
        //       thumbnail:
        //         '/thumbnail/https://heyboss.heeyo.ai/design-system/video_0gPfq4jj.png',
        //       url: response.file.download_url,
        //       isSelected: true,
        //       type: 'video',
        //     },
        //   ])
        // }
      }
    } catch (error) {
      console.error('AI generation failed:', error)
      toast.error(t('generating_error'))
    } finally {
      setCreateDisable(false)
    }
  }

  const handleAddToList = async () => {
    setIsUploading(true)
    const assets = resources.filter((img) => img.isSelected)

    try {
      await Promise.all(
        assets.map(async (asset) => {
          await fetch('/api/me/assets', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-request-id': nanoid(12),
            },
            body: JSON.stringify({
              asset_url: asset.url,
              type: asset.type,
              source: 'creation',
            }),
          })
        }),
      )

      setResources([])
      setIsEditing(true) // Reset to editing mode
      setDesc('') // Clear the prompt

      toast.success(t('upload_success'), {
        position: 'top-center',
      })

      // Close modal immediately and trigger loading after close
      if (onAddToLibraryComplete) {
        onAddToLibraryComplete()
      } else {
        popModal('AIGeneratorV2Modal')
      }

      // Start loading state after modal closes
      setTimeout(() => {
        onGenerationStart?.(assets.length)
      }, 100) // Small delay to ensure modal is closed
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error(t('upload_error'))
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemoveImage = useCallback((urlToRemove: string) => {
    setResources((items) => {
      const newItems = items.filter((item) => item.url !== urlToRemove)
      if (newItems.length === 0) {
        setIsEditing(true)
      }
      return newItems
    })
  }, [])

  const handleRatioSelect = useCallback((ratio: string) => {
    setSelectedRatio(ratio)
    setIsTooltipOpen(false)
  }, [])

  const handleTooltipClick = useCallback(() => {
    setIsTooltipOpen((prev) => !prev)
  }, [])

  const handleEditPrompt = useCallback(() => {
    setIsEditing(true)
  }, [])

  const value = {
    desc,
    setDesc,
    activeType,
    setActiveType,
    resources,
    setResources,
    createDisable,
    isUploading,
    selectedRatio,
    setSelectedRatio,
    isTooltipOpen,
    setIsTooltipOpen,
    isEditing,
    setIsEditing,
    canUpload,
    aiGenerator,
    handleAddToList,
    handleRemoveImage,
    handleRatioSelect,
    handleTooltipClick,
    handleEditPrompt,
  }

  return (
    <AIGeneratorContext.Provider value={value}>
      {children}
    </AIGeneratorContext.Provider>
  )
}

export function useAIGenerator() {
  const context = useContext(AIGeneratorContext)
  if (context === undefined) {
    throw new Error('useAIGenerator must be used within a AIGeneratorProvider')
  }
  return context
}
